// =============================================
// useDomainRejections — lista domen odrzuconych (nie będą przedłużane)
//
// W UI to jedno pole tekstowe "jedna domena na linię", w bazie jeden wiersz
// na domenę (`domain_rejections`) — dzięki temu każda ma własną notatkę
// i datę dodania, a zapis to czysty diff zamiast nadpisywania blobu.
//
// Celowo BEZ pola "załatwione": ostrzeżenie w panelu to przecięcie tej listy
// ze stanem tabeli `websites`. Usuwasz domenę z panelu → nic nie pasuje →
// komunikat znika sam. Zero dodatkowego klikania.
// =============================================
import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";
import { normalizeDomain } from "@/services/aftermarketService";

export interface DomainRejection {
  id: string;
  user_id: string;
  domain: string;
  note?: string | null;
  created_at: string;
}

/** Tekst z textarea → znormalizowane, odduplikowane domeny (pomija puste i komentarze). */
export function parseRejectionList(text: string): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  for (const rawLine of (text || "").split(/\r?\n/)) {
    const line = rawLine.trim();
    if (!line || line.startsWith("#")) continue;
    // dopuszczamy "domena.pl — powód" / "domena.pl, powód" — bierzemy pierwszy token
    const first = line.split(/[\s,;]+/)[0];
    const domain = normalizeDomain(first);
    if (!domain || seen.has(domain)) continue;
    seen.add(domain);
    out.push(domain);
  }
  return out;
}

/** Różnica listy zapisanej vs nowej — do podglądu PRZED zapisem. */
export function diffRejections(current: string[], next: string[]): { added: string[]; removed: string[] } {
  const cur = new Set(current);
  const nxt = new Set(next);
  return {
    added: next.filter((d) => !cur.has(d)),
    removed: current.filter((d) => !nxt.has(d)),
  };
}

export const useDomainRejections = () => {
  const [rejections, setRejections] = useState<DomainRejection[]>([]);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();
  const { toast } = useToast();

  const fetchRejections = useCallback(async () => {
    if (!user) return;
    try {
      setLoading(true);
      const { data, error } = await (supabase as any)
        .from("domain_rejections")
        .select("*")
        .order("domain", { ascending: true });
      if (error) throw error;
      setRejections((data || []) as DomainRejection[]);
    } catch (err) {
      toast({
        title: "Błąd",
        description: err instanceof Error ? err.message : "Nie udało się pobrać listy odrzuconych domen",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }, [user?.id, toast]);

  useEffect(() => {
    if (user?.id) fetchRejections();
  }, [user?.id, fetchRejections]);

  /** Zastępuje całą listę: dopisuje nowe, kasuje te, których już nie ma w tekście. */
  const replaceList = useCallback(
    async (text: string): Promise<boolean> => {
      if (!user) return false;
      const next = parseRejectionList(text);
      const current = rejections.map((r) => r.domain);
      const { added, removed } = diffRejections(current, next);

      if (added.length === 0 && removed.length === 0) {
        toast({ title: "Bez zmian", description: "Lista odrzuconych domen jest już taka sama" });
        return true;
      }

      try {
        if (removed.length > 0) {
          const { error } = await (supabase as any).from("domain_rejections").delete().in("domain", removed);
          if (error) throw error;
        }
        if (added.length > 0) {
          const { error } = await (supabase as any)
            .from("domain_rejections")
            .upsert(
              added.map((domain) => ({ user_id: user.id, domain })),
              { onConflict: "domain" },
            );
          if (error) throw error;
        }
        await fetchRejections();
        toast({
          title: "Zapisano listę odrzuconych",
          description: `Dodano ${added.length}, usunięto ${removed.length}`,
        });
        return true;
      } catch (err) {
        toast({
          title: "Błąd",
          description: err instanceof Error ? err.message : "Nie udało się zapisać listy",
          variant: "destructive",
        });
        return false;
      }
    },
    [user, rejections, fetchRejections, toast],
  );

  /** Zdejmuje pojedynczą domenę z listy odrzuconych (np. jednak zostaje). */
  const removeRejection = useCallback(
    async (domain: string): Promise<boolean> => {
      try {
        const { error } = await (supabase as any).from("domain_rejections").delete().eq("domain", domain);
        if (error) throw error;
        setRejections((prev) => prev.filter((r) => r.domain !== domain));
        return true;
      } catch (err) {
        toast({
          title: "Błąd",
          description: err instanceof Error ? err.message : "Nie udało się usunąć wpisu",
          variant: "destructive",
        });
        return false;
      }
    },
    [toast],
  );

  return { rejections, loading, replaceList, removeRejection, refreshRejections: fetchRejections };
};
